import moment from "moment";

const DATE_FORMATS = [
  "YYYY-MM-DD",
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DDTHH:mm:ss",
  "YYYY-MM-DDTHH:mm:ss.SSS",
  "dddd, DD MMMM YYYY",
  "dddd, D MMMM YYYY",
];

const TIME_FORMATS = [
  "HH:mm:ss",
  "HH:mm",
  "H:mm:ss",
  "H:mm",
  "h:mma",
  "h:mm A",
  "hh:mma",
  "hh:mm A",
];

const APPOINTMENT_DEPOSIT_MUTATION_FIELDS = new Set([
  "totaldeposit",
  "deposit_payment_by",
  "stripe_token_id",
  "stripe_payment_method_id",
  "previous_deposit_paid",
  "service_deposit",
  "deposit_booked_online",
  "isDepositTabOpen",
]);

const isRecord = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const firstPresent = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "") ?? "";

const hasValue = (value) => value !== undefined && value !== null && value !== "";

export const getAppointmentFormHydrationState = ({
  detail,
  appointmentStatus,
  serviceStatus,
  serviceOptions,
  requireService = false,
} = {}) => {
  if (appointmentStatus === "loading" || (requireService && serviceStatus === "loading")) {
    return { phase: "loading", interactive: false };
  }

  const source = isRecord(detail) ? detail : {};
  const hasAppointment = [
    source.id,
    firstPresent(source.client_id, source.client?.id),
    firstPresent(source.dateof, source.showdate, source.start_datetime),
    firstPresent(source.start_time, source.showstartdate, source.start_datetime),
  ].every(hasValue);
  const serviceId = firstPresent(source.service_id, source.service?.id);
  const hasStaff = !requireService || hasValue(firstPresent(source.staff_id, source.staff?.id));
  const hasService = !requireService || (
    hasValue(serviceId)
    && (
      hasValue(source.service?.name)
      || (Array.isArray(serviceOptions) && serviceOptions.some((option) => (
        isRecord(option)
        && String(firstPresent(option.value, option.id)) === String(serviceId)
        && hasValue(firstPresent(option.label, option.name))
      )))
    )
  );

  if (appointmentStatus === "failed") {
    return { phase: "error", interactive: false };
  }
  if (hasAppointment && hasService && hasStaff) {
    return { phase: "ready", interactive: true };
  }
  if (requireService && serviceStatus === "failed") {
    return { phase: "error", interactive: false };
  }
  if (appointmentStatus === "succeeded" || (requireService && serviceStatus === "succeeded")) {
    return { phase: "error", interactive: false };
  }
  return { phase: "loading", interactive: false };
};

const parseDate = (value) => {
  if (!value) return null;
  if (moment.isMoment(value)) return value.isValid() ? value : null;

  const parsed = moment(value, DATE_FORMATS, true);
  return parsed.isValid() ? parsed : null;
};
const parseTime = (value) => {
  if (!value) return null;
  if (moment.isMoment(value)) return value.isValid() ? value : null;

  const parsed = moment(value, TIME_FORMATS, true);
  return parsed.isValid() ? parsed : null;
};

const parseDurationMinutes = (value) => {
  if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
    return value;
  }

  if (typeof value !== "string") return null;
  const text = value.trim();
  if (!text) return null;

  if (/^\d+(?:\.\d+)?$/.test(text)) {
    const minutes = Number(text);
    return Number.isFinite(minutes) && minutes >= 0 ? minutes : null;
  }

  const match = /^(\d{1,3}):(\d{2})$/.exec(text);
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return hours * 60 + minutes;
};

export const normalizeAppointmentDate = (value) => {
  const parsed = parseDate(value);
  return parsed ? parsed.format("YYYY-MM-DD") : "";
};

export const normalizeAppointmentTime = (value) => {
  const parsed = parseTime(value);
  return parsed ? parsed.format("HH:mm:ss") : "";
};

export const isRepeatedAppointment = (value) =>
  value === true || value === 1 || value === "1" || value === "Yes";

export const getAppointmentEditScope = (values = {}) =>
  isRepeatedAppointment(values.repeat_appointment)
    ? (values.edit_scope === "future" ? "future" : "individual")
    : "series";

export const hasUnsupportedAppointmentDepositSelection = (values = {}) => (
  isRecord(values)
  && values.deposit_payment_by !== undefined
  && values.deposit_payment_by !== null
  && values.deposit_payment_by !== ""
);

export const normalizeAppointmentMutationPayload = (values = {}) => {
  if (!isRecord(values)) return values;

  const scheduleValues = Object.keys(values).reduce((result, field) => {
    if (!APPOINTMENT_DEPOSIT_MUTATION_FIELDS.has(field)) {
      result[field] = values[field];
    }
    return result;
  }, {});

  const normalizedDate = normalizeAppointmentDate(scheduleValues.dateof);
  const normalizedTime = normalizeAppointmentTime(scheduleValues.start_time);

  return {
    ...scheduleValues,
    ...(normalizedDate ? { dateof: normalizedDate } : {}),
    ...(normalizedTime ? { start_time: normalizedTime } : {}),
    ...(scheduleValues.status === "Pencilled In" ? { status: "Scheduled" } : {}),
  };
};

export const getAppointmentLocalTemporalFields = (values = {}) => {
  const source = isRecord(values) ? values : {};
  const date = normalizeAppointmentDate(source.dateof);
  const startTime = normalizeAppointmentTime(source.start_time);

  if (!date || !startTime) {
    return {
      showdate: "",
      showstartdate: "",
      showenddate: "",
    };
  }

  const start = moment(`${date} ${startTime}`, "YYYY-MM-DD HH:mm:ss", true);
  if (!start.isValid()) {
    return {
      showdate: "",
      showstartdate: "",
      showenddate: "",
    };
  }

  const duration = parseDurationMinutes(source.duration);
  const end = duration === null ? null : start.clone().add(duration, "minutes");

  return {
    showdate: date,
    showstartdate: start.format("YYYY-MM-DD HH:mm:ss"),
    showenddate: end && end.isValid()
      ? end.format("YYYY-MM-DD HH:mm:ss")
      : "",
  };
};

const normalizeDisplayDateTime = (value) => {
  const parsed = parseDate(value);
  return parsed ? parsed.format("YYYY-MM-DD HH:mm:ss") : "";
};

const normalizeDisplayDate = (value) => {
  const parsed = parseDate(value);
  return parsed ? parsed.format("YYYY-MM-DD") : "";
};

/**
 * Build the request used to reload the edited appointment detail.
 *
 * The update API stores UTC datetimes, but the view API accepts salon-local
 * show* values. Form values are therefore authoritative for a normal edit;
 * response/detail values are only compatibility fallbacks for older callers
 * and status-only updates.
 */
export const buildAppointmentDetailRefreshPayload = ({
  values = {},
  response = {},
  detail = {},
} = {}) => {
  const safeValues = isRecord(values) ? values : {};
  const safeResponse = isRecord(response) ? response : {};
  const safeDetail = isRecord(detail) ? detail : {};
  const submittedLocal = getAppointmentLocalTemporalFields(safeValues);
  const detailLocal = getAppointmentLocalTemporalFields(safeDetail);

  const showstartdate = firstPresent(
    submittedLocal.showstartdate,
    normalizeDisplayDateTime(safeResponse.showstartdate),
    normalizeDisplayDateTime(safeDetail.showstartdate),
    detailLocal.showstartdate,
  );
  const showenddate = firstPresent(
    submittedLocal.showenddate,
    normalizeDisplayDateTime(safeResponse.showenddate),
    normalizeDisplayDateTime(safeDetail.showenddate),
    detailLocal.showenddate,
  );
  const showdate = firstPresent(
    submittedLocal.showdate,
    normalizeDisplayDate(safeResponse.showdate),
    normalizeDisplayDate(safeDetail.showdate),
    detailLocal.showdate,
    showstartdate ? showstartdate.slice(0, 10) : "",
  );

  return {
    id: firstPresent(safeResponse.id, safeValues.id, safeDetail.id),
    client_id: firstPresent(
      safeResponse.client_id,
      safeValues.client_id,
      safeDetail.client_id,
      safeDetail.client?.id,
    ),
    showdate,
    showstartdate,
    showenddate,
  };
};
